import React, { useState, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { getSlugPrefix } from '@/utils/slug';
import { DatePicker, ColorPicker, ConfigProvider, Select, message, Modal, Table } from 'antd';
import { Input, Dropdown, Radio , Button } from '@/components/atoms';
import { MultiSelect } from '@/components/atoms/MultiSelect/MultiSelect';
import { Dialog } from '@/components/organisms/Modal/Dialog';
import StudentSelectionContent from '@/components/Modal/StudentSelectionContent';
import dayjs from 'dayjs';
import locale from 'antd/locale/ko_KR';
import AcademyStudentTable from '@/components/organisms/Table/AcademyStudentTable';
import { Icon } from '@/icons';
import { adminTextbookApi, academyLearningScheduleApi } from '@/services/api';
const { RangePicker } = DatePicker;


interface Student {
  id: string;
  userId?: string;
  name: string;
  academy?: string;
  grade?: string;
  status?: 'active' | 'inactive';
  no?: number;
  academyCode?: string;
  studentName?: string;
  birthDate?: string;
  loginId?: string;
  email?: string;
}

type TextbookBlock = {
  id: string;
  type: string; // 카테고리
  textbook: string; // 교재 ID
  unitIds: Array<string | number>; // 선택 유닛 ID들
  textbookOptions: { value: string; label: string }[];
  unitOptions: { value: string | number; label: string }[];
  color?: string;
};

type PreviewRow = { date: string; capacity: number; assigned: number };

const AdminLearningDetail: React.FC = () => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const slugPrefix = getSlugPrefix(pathname);

  const [selectedStudents, setSelectedStudents] = useState<Student[]>([]);
  const [isStudentSelectionModalOpen, setIsStudentSelectionModalOpen] = useState(false);
  const [selectedColor, setSelectedColor] = useState<string>('#1890ff');
  const [textbooks, setTextbooks] = useState<TextbookBlock[]>([
    { id: 'textbook-default', type: '', textbook: '', unitIds: [], textbookOptions: [], unitOptions: [], color: '#1890ff' }
  ]);
  const [maxUnitsPerDay, setMaxUnitsPerDay] = useState<number>(2);
  const [dateRange, setDateRange] = useState<any>(null);
  const [learningTitle, setLearningTitle] = useState<string>('');
  const [scoringRequired, setScoringRequired] = useState<boolean>(true);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);

  const typeToDefaultColor = useMemo(() => new Map<string, string>([
    ['파닉스데이', '#1890ff'],
    ['스타터_파닉스', '#2f54eb'],
    ['스타터_뉘앙스', '#722ed1'],
    ['스타터_스트럭처', '#52c41a'],
    ['에세이', '#fa8c16'],
    ['통번역', '#faad14'],
    ['리딩북', '#13c2c2'],
  ]), []);
  const typeOptions = [
    { value: '파닉스데이', label: '파닉스 데이' },
    { value: '스타터_파닉스', label: '스타터 파닉스' },
    { value: '스타터_뉘앙스', label: '스타터 뉘앙스' },
    { value: '스타터_스트럭처', label: '스타터 스트럭처' },
    { value: '에세이', label: '에세이' },
    { value: '통번역', label: '통번역' },
    { value: '리딩북', label: '리딩북' },
  ];

  // 유형 선택 → 교재 목록 로드
  const loadTextbooksByType = async (idx: number, typeLabel: string) => {
    try {
      const res = await adminTextbookApi.list({ q: '', category: typeLabel, page: 0, size: 100 });
      const data = res?.data || res; 
      const page = data?.data || data; 
      const content = page?.content || [];
      const opts = content.map((t: any) => ({ value: String(t.id), label: t.name }));
      setTextbooks(prev => prev.map((tb, i) => i === idx ? { 
        ...tb, 
        type: typeLabel, 
        textbook: '', 
        unitIds: [], 
        textbookOptions: opts, 
        unitOptions: [], 
        color: typeToDefaultColor.get(typeLabel) || tb.color 
      } : tb));
    } catch (e) { 
      message.error('교재 목록을 불러오지 못했습니다.'); 
    }
  };

  // 교재 선택 → 유닛 목록 로드
  const loadUnitsByTextbook = async (idx: number, textbookId: string) => {
    try {
      const res = await adminTextbookApi.listUnits(textbookId);
      const data = res?.data || res; 
      const list = data?.data || data || [];
      const opts = list.map((u: any) => ({ value: String(u.id), label: u.title }));
      setTextbooks(prev => prev.map((tb, i) => i === idx ? { ...tb, textbook: textbookId, unitOptions: opts } : tb));
    } catch (e) { 
      message.error('유닛 목록을 불러오지 못했습니다.'); 
    }
  };






  const handleLoadStudents = () => setIsStudentSelectionModalOpen(true);
  const handleStudentSelectionConfirm = (students: Student[]) => {
    setSelectedStudents(prev => {
      const map = new Map<string, Student>();
      [...prev, ...students].forEach((s: Student) => {
        map.set(String(s.id), s);
      });
      return Array.from(map.values());
    });
    setIsStudentSelectionModalOpen(false);
  };
  const handleStudentSelectionCancel = () => setIsStudentSelectionModalOpen(false);

  const handleAddTextbook = () => setTextbooks(prev => [...prev, { 
    id: `textbook-${Date.now()}`, 
    type: '', 
    textbook: '', 
    unitIds: [], 
    textbookOptions: [], 
    unitOptions: [], 
    color: '#1890ff' 
  }]);
  const handleRemoveTextbook = (id: string) => setTextbooks(prev => prev.filter(tb => tb.id !== id));
  const handleTypeChange = (idx: number, typeLabel: string) => { loadTextbooksByType(idx, typeLabel); };
  const handleBookChange = (idx: number, bookId: string) => { loadUnitsByTextbook(idx, bookId); };
  const handleUnitIdsChange = (idx: number, unitIds: (string | number)[]) => setTextbooks(prev => prev.map((tb, i) => i === idx ? { ...tb, unitIds } : tb));
  const buildUnitLinks = () => {
    const dedup = new Map<number, { unitId: number; textbookId?: number }>();
    textbooks.forEach(tb => {
      const numericTextbookId = tb.textbook ? Number(tb.textbook) : undefined;
      const sanitizedTextbookId = numericTextbookId !== undefined && !Number.isNaN(numericTextbookId)
        ? numericTextbookId
        : undefined;
      (tb.unitIds || []).forEach((raw) => {
        const unitId = Number(raw);
        if (Number.isNaN(unitId)) return;
        if (!dedup.has(unitId)) {
          dedup.set(unitId, { unitId, textbookId: sanitizedTextbookId });
        }
      });
    });
    return Array.from(dedup.values());
  };

  const previewRows = useMemo<PreviewRow[]>(() => {
    if (!dateRange || !dateRange?.length) return [];
    const startValue = dateRange[0]?.format?.('YYYY-MM-DD') || dateRange[0];
    const endValue = dateRange[1]?.format?.('YYYY-MM-DD') || dateRange[1];
    if (!startValue || !endValue) return [];
    const start = new Date(startValue);
    const end = new Date(endValue);
    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return [];
    const totalUnitsSelected = textbooks.reduce((sum, tb) => sum + (tb.unitIds?.length || 0), 0);
    if (totalUnitsSelected <= 0) return [];
    const rows: PreviewRow[] = [];
    let remaining = totalUnitsSelected * Math.max(1, selectedStudents.length || 1);
    let carry = 0;
    for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
      const capacity = maxUnitsPerDay + carry;
      const assigned = Math.min(capacity, remaining);
      remaining -= assigned;
      carry = capacity - assigned;
      rows.push({ date: d.toISOString().slice(0, 10), capacity, assigned });
      if (remaining <= 0 && carry === 0) {
        break;
      }
    }
    return rows;
    // [QA Fix] 학습 미리보기 계산
  }, [dateRange, textbooks, selectedStudents.length, maxUnitsPerDay]);

  const handlePreviewClick = () => {
    if (!dateRange || !dateRange.length) {
      message.error('학습 기간을 선택해 주세요.');
      return;
    }
    const hasUnits = textbooks.some(tb => (tb.unitIds?.length || 0) > 0);
    if (!hasUnits) {
      message.error('배정할 유닛을 선택해 주세요.');
      return;
    }
    setIsPreviewOpen(true);
  };

  dayjs.locale('ko');
  return (
    <main className='flex flex-col flex-1 py-8'>
        
    <div className='flex flex-row justify-between items-center pb-5'>
        <h1 className='heading1'>학습등록</h1>
    </div>
    <section>
    <ul>
        <li className='flex py-5 border-b border-gray-100'>
            <p className='flex-none max-w-[150px] w-full text-gray-500'>학습명</p>
            <Input 
              placeholder='학습명을 입력하세요' 
              className='max-w-[500px]'
              value={learningTitle}
              onChange={(e) => setLearningTitle((e.target as HTMLInputElement).value)}
            />
        </li>

        <li className='flex py-5 border-b border-gray-100'>
            <p className='flex-none max-w-[150px] w-full text-gray-500'>교재선택</p>
            <section className='flex flex-col gap-5 w-full'>
                {textbooks.map((block, index) => (
                    <div key={block.id} className='flex gap-2 items-start w-full'>
                    <div className='flex flex-col gap-2 w-full max-w-[300px]'>
                    <p className='text-sm text-gray-500'>유형</p>
                        <Dropdown 
                            placeholder='유형을 선택하세요' 
                            options={typeOptions.map(t => ({ value: t.value, label: t.label }))}
                            value={block.type || null}
                            onChange={(v) => handleTypeChange(index, v as string)}
                        />
                    </div>
                    <div className='flex flex-col gap-2 w-full max-w-[300px]'>
                    <p className='text-sm text-gray-500'>교재</p>
                        <Dropdown
                            placeholder='교재를 선택하세요'
                            options={block.textbookOptions || []}
                            value={block.textbook || null}
                            onChange={(v) => handleBookChange(index, v as string)}
                        />
                    </div>
                    <div className='flex flex-col gap-2 w-full max-w-[300px]'>
                    <p className='text-sm text-gray-500'>유닛</p>
                        <MultiSelect
                            allowClear
                            placeholder='유닛을 선택하세요'
                            options={block.unitOptions || []}
                            value={Array.isArray(block.unitIds) ? block.unitIds : (block.unitIds ? [block.unitIds] : null)}
                            onChange={(vals) => handleUnitIdsChange(index, vals)}
                        />
                    </div>
                        {index > 0 && (
                            <Button 
                                outline 
                                intent='danger' 
                                className='flex flex-none gap-2 items-center w-20'
                                onClick={() => handleRemoveTextbook(block.id)}
                            >
                                삭제
                            </Button>
                        )}
                    </div>
                ))}
                <div className='flex gap-2 w-full'>
                    <Button 
                        outline 
                        className='flex flex-none gap-2 items-center w-40'
                        onClick={handleAddTextbook}
                    >
                        <Icon name='plus' size={20} className='*:fill-indigo-500' />
                        교재 추가하기
                    </Button>
                </div>
            </section>
        </li>

        <li className='flex py-5 border-b border-gray-100'>
            <p className='flex-none max-w-[150px] w-full text-gray-500'>일일 최대 학습 가능한 유닛 갯수</p>
            <div className='flex gap-3 items-center'>
                <Select 
                  value={String(maxUnitsPerDay)} 
                  onChange={(v) => setMaxUnitsPerDay(Number(v))} 
                  style={{ width: 120 }} 
                  size='large'
                  options={Array.from({ length: 10 }, (_, i) => ({ value: String(i + 1), label: String(i + 1) }))}
                />
                <span className='text-gray-500'>(1~10, 미소비 수량은 익일 가산)</span>
            </div>
        </li>

        <li className='flex py-5 border-b border-gray-100'>
            <p className='flex-none max-w-[150px] w-full text-gray-500'>학습 기간</p>
            <ConfigProvider
                locale={locale}
                theme={{
                    token: {
                        colorPrimary: '#6366f1',
                        borderRadius: 10,
                        fontSize: 16,
                        colorBorder: '#E5E7EB',
                        colorBorderSecondary: '#E5E7EB',
                        colorTextPlaceholder: '#9CA3AF',
                        colorIcon: '#9CA3AF',
                    },
                }}
            >
                <div className='flex gap-2 items-center'>
                    <DatePicker.RangePicker
                        format="YYYY-MM-DD"
                        style={{ width: 500, height: 44 }}
                        onChange={(rng) => setDateRange(rng)}
                    />
                </div>
            </ConfigProvider>
        </li>



        <li className='flex py-5 border-b border-gray-100'>
            <p className='flex-none max-w-[150px] w-full text-gray-500'>캘린더 색상</p>
            <ConfigProvider
                                locale={locale}
                                theme={{
                                    token: {
                                        colorPrimary: '#6366f1', // indigo-500
                                        fontSize: 16,
                                        colorBorder: '#E5E7EB', // 기본 테두리 색상 (gray-300)
                                        colorBorderSecondary: '#E5E7EB', // 보조 테두리 색상 (gray-200)
                                        colorTextPlaceholder: '#9CA3AF', // 플레이스홀더 색상 (gray-400)
                                        colorIcon: '#9CA3AF', // 아이콘 색상 (gray-400)
                                        controlHeight: 32,
                                        
                                    },
                                }}
                            >
                                
            <ColorPicker
                    value={selectedColor}
                    onChange={(color) => setSelectedColor(color.toHexString())}
                    
                    size="large"
                  />
                  </ConfigProvider>
        </li>
        
        <li className='flex py-5 border-b border-gray-100'>
            <p className='flex-none max-w-[150px] w-full text-gray-500'>채점 여부</p>
            <div className='flex gap-10'>
                <Radio label='채점함' name='scoring' onChange={() => setScoringRequired(true)} checked={scoringRequired}/>
                <Radio label='채점 불필요' name='scoring' onChange={() => setScoringRequired(false)} checked={!scoringRequired} />
            </div>
        </li>
        <li className='flex py-5 border-b border-gray-100'>
            <p className='flex-none max-w-[150px] w-full text-gray-500'>학습 대상</p>
            <section className='w-full'>
                <div className='flex gap-2 w-full'>
                    <Input
                      placeholder='학습 대상자를 선택해주세요'
                      className='max-w-[500px] w-full cursor-pointer'
                      readOnly
                      onClick={handleLoadStudents}
                      value={
                        selectedStudents.length
                          ? selectedStudents.map((s) => s.name || s.studentName || '').filter(Boolean).join(', ')
                          : ''
                      }
                    />{/* 학습 대상 입력은 모달 선택 결과를 표시하기 위한 읽기 전용 필드 */}
                    <Button type='button' className='flex-none' onClick={handleLoadStudents}>
                      불러오기
                    </Button>
                </div>
                <p className='mt-2 text-sm text-gray-500'>불러오기 버튼을 눌러 학원 소속 학생을 검색/선택할 수 있습니다.</p>
                <div className='mt-5 max-h-[400px] overflow-y-auto'>
                    <AcademyStudentTable 
                        showSearch={false} 
                        data={selectedStudents as any} 
                    selectedStudents={selectedStudents as any} 
                    onStudentSelect={() => {}} 
                    onSelectAll={()=>{}} 
                    type='display' 
                    displayType='learning'
                    showPagination={false}
                    showAcademyCode={false}
                    onDelete={(student) => {
                        const isLastStudent = selectedStudents.length <= 1;
                        Modal.confirm({
                            title: isLastStudent
                                ? '해당 학생까지 삭제할 경우 숙제 전체가 사라집니다. 삭제하시겠습니까?'
                                : '학생 삭제',
                            content: isLastStudent
                                ? '모든 학생을 삭제하면 학습이 더 이상 유지되지 않습니다.'
                                : '해당 학생을 삭제하시겠습니까?',
                            okText: '삭제',
                            cancelText: '취소',
                            onOk: () => {
                                setSelectedStudents(prev => prev.filter(s => s.id !== student.id));
                                message.success('학생이 삭제되었습니다.');
                            }
                        });
                    }}
                    />
                </div>
            </section>
        </li>
        <li className='flex py-5 border-b border-gray-100'>
            <p className='flex-none max-w-[150px] w-full text-gray-500'>등록자</p>
            <p>{localStorage.getItem('academy_user_name') || '학원 관리자'}</p>
        </li>

        <li className='flex py-5 border-b border-gray-100'>
            <p className='flex-none max-w-[150px] w-full text-gray-500'>등록일</p>
            <p>{new Date().toISOString().slice(0,10)}</p>
        </li>
    </ul>
    <div className='flex gap-2 justify-between my-5'>
        <Button outline className='w-28' onClick={() => navigate(`${slugPrefix}/academy/learning/assign/list`)}>뒤로가기</Button>
        <div className='flex gap-2'>
            <Button outline intent='primary' className='w-32' onClick={handlePreviewClick}>
                미리보기
            </Button>
            <Button className='w-28' onClick={async () => {
                try {
                    if (!dateRange || !dateRange.length) { message.error('학습 기간을 선택해 주세요'); return; }
                    if (selectedStudents.length === 0) { message.error('학습 대상 학생을 선택해 주세요'); return; }
                    
                    const startStr = dateRange[0]?.format?.('YYYY-MM-DD') || String(dateRange[0]);
                    const endStr = dateRange[1]?.format?.('YYYY-MM-DD') || String(dateRange[1]);
                    
                    const items = selectedStudents.map(s => ({
                        id: null,
                        studentUserId: Number(s.userId || s.id) || undefined,
                        title: (learningTitle || '').trim() || '학습',
                        startDate: startStr,
                        endDate: endStr,
                        backgroundColor: selectedColor,
                        borderColor: selectedColor,
                        maxUnitsPerDay: maxUnitsPerDay,
                        scoringRequired
                    }));
                    
                    // 1) 학생별 스케줄 저장
                    const saveResult = await academyLearningScheduleApi.save({ items, learningId: undefined });
                    const schedules = saveResult?.data?.schedules || [];
                    
                    // 2) 각 스케줄에 유닛 연결
                    const unitLinks = buildUnitLinks();
                    const unitIds = unitLinks.map(link => link.unitId);
                    if (unitIds.length === 0) {
                        message.error('배정할 유닛을 선택해 주세요.');
                        return;
                    }
                    
                    for (const schedule of schedules) {
                        await academyLearningScheduleApi.linkUnits(schedule.id, { unitIds, unitLinks });
                    }
                    
                    message.success('학습이 저장되었습니다.'); 
                    navigate(`${slugPrefix}/academy/learning/assign/list`);
                } catch (e) {
                    console.error('학습 저장 실패:', e);
                    message.error('학습 저장에 실패했습니다.');
                }
            }}>저장</Button>
        </div>
    </div>


    
    </section>

<Modal
          open={isStudentSelectionModalOpen}
          onCancel={handleStudentSelectionCancel}
          footer={null}
          width={800}
          destroyOnClose
          maskClosable={false}
          bodyStyle={{ padding: 0 }}
        >
          <StudentSelectionContent
            onOk={(sel) => handleStudentSelectionConfirm(sel as Student[])}
            onCancel={handleStudentSelectionCancel}
            title='학생 선택'
            showAcademyCode={false}
          />
        </Modal>

<Dialog
          open={isPreviewOpen}
          onCancel={() => setIsPreviewOpen(false)}
          onOk={() => setIsPreviewOpen(false)}
          okText='닫기'
          className='!max-w-[900px] !w-full'
        >
          <div className='p-5'>
            <div className='mb-3 text-base font-semibold'>학습 프로세스 미리보기 (누적 최대치 가산)</div>
            <Table
              size='small'
              pagination={false}
              rowKey='date'
              dataSource={previewRows}
              columns={[
                { title: '날짜', dataIndex: 'date' },
                { title: '당일 허용치', dataIndex: 'capacity' },
                { title: '예상 배정 수', dataIndex: 'assigned' },
              ]}
              locale={{ emptyText: '학습 기간과 유닛을 선택하면 미리보기가 제공됩니다.' }}
            />
          </div>
        </Dialog>
    </main>
    
  );
};

export default AdminLearningDetail; 
